import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import PageContainer from "../../../components/global/PageContainer"
import { useTranslation } from "react-i18next"
import { AuthContext } from "../../../controllers/auth"
import { StoriesContext } from "../../../controllers/stories"
import FadeFromTop from "../../../components/animations/FadeFromTop"
import Table from "../../../components/global/Table"
import { colors } from "../../../services/config/colors"
import TableRow from "../../../components/global/TableRow"
import {
  ButtonBase,
  Checkbox,
  Fab,
  Grow,
  IconButton,
  Stack,
  TableCell,
} from "@mui/material"
import Text from "../../../components/global/Text"
import { useNavigate } from "react-router-dom"
import StoriesCompactFilters from "../../../components/stories/StoriesCompactFilters"
import { CloseRounded, SendRounded } from "@mui/icons-material"
import Modal from "../../../components/global/Modal"
import Input from "../../../components/global/Input"
import Button from "../../../components/global/Button"
import { CSVLink } from "react-csv"
import slugify from "slugify"

const Stories = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { team } = useContext(AuthContext)
  const {
    getStoriesData,
    storiesDataLoading,
    storiesList,
    storiesListNextToken,
    selectedStories,
    setSelectedStories,
    loadMoreStories,
  } = useContext(StoriesContext)

  // get data
  useEffect(() => {
    getStoriesData()
  }, [team])

  // export stories modal
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false)

  // table heading items
  const headingItems = useMemo(() => {
    return [
      <ButtonBase
        style={{
          width: 42,
          height: 42,
          borderRadius: "100%",
          opacity: selectedStories.length ? 1 : 0,
          transition: "100ms",
        }}
        onClick={() => setSelectedStories([])}
        disabled={!selectedStories.length}
        disableRipple
      >
        <div
          style={{
            width: 18,
            height: 18,
            backgroundColor: colors.primary,
            borderRadius: 2,
          }}
        >
          <Text
            fontSize={26}
            lineHeight="12px"
            fontWeight={500}
            textAlign="center"
            color={colors.textWhite}
          >
            -
          </Text>
        </div>
      </ButtonBase>,
      <>
        {t("stories")}{" "}
        <span style={{ color: colors.textSecondary }}>
          ({storiesList.length})
        </span>
      </>,
      t("sdg"),
      t("topic"),
    ]
  }, [t, storiesList, selectedStories])

  // page container onScroll event to fetch new items on scroll
  const fetchingMoreItems = useRef<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)

  const onScroll = async () => {
    const mainContainer = document.getElementById("page-container")!

    if (
      mainContainer.scrollTop + 50 >=
        mainContainer.scrollHeight - window.innerHeight &&
      !fetchingMoreItems.current &&
      storiesListNextToken &&
      !storiesDataLoading
    ) {
      fetchingMoreItems.current = true
      setLoadingMore(true)

      await loadMoreStories()

      fetchingMoreItems.current = false
      setLoadingMore(false)
    }
  }

  return (
    <PageContainer
      title={t("stories")}
      breadcrumbs={[{ title: t("stories") }]}
      withFilters={false}
      withDatePicker={false}
      withExportButton={false}
      onScroll={onScroll}
      paddingTop={0}
    >
      <StoriesCompactFilters />
      <FadeFromTop style={{ marginTop: 40 }}>
        <Table
          headingItems={headingItems}
          loading={storiesDataLoading}
          loadingMore={loadingMore}
        >
          {storiesList.map((story, index) => {
            const sdg = story.sdgs.find((item) => item.primary)?.sdg

            return (
              <TableRow
                index={index}
                key={story.id}
                onClick={() =>
                  navigate(`${window.location.pathname}/${story.id}`)
                }
              >
                <TableCell>
                  <Checkbox
                    checked={selectedStories.some(
                      (item) => item.id === story.id
                    )}
                    onChange={(_, checked) => {
                      if (checked) {
                        setSelectedStories((current) => [...current, story])
                      } else {
                        setSelectedStories((current) =>
                          current.filter((item) => item.id !== story.id)
                        )
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </TableCell>
                <TableCell>
                  <Stack
                    direction="row"
                    alignItems="center"
                    style={{ gap: 20 }}
                  >
                    <img
                      src={story.image}
                      style={{
                        width: 30,
                        minWidth: 30,
                        height: 30,
                        borderRadius: 4,
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                      alt=""
                    />
                    <Text fontSize={14} fontWeight={500}>
                      {story.translation.title}
                    </Text>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Text fontSize={14} fontWeight={500}>
                    {`${t("sdg")} ${Number(sdg?.code)} - ${sdg?.name}`}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text fontSize={14} fontWeight={500}>
                    {story.topics.find((item) => item.primary)?.topic.name}
                  </Text>
                </TableCell>
              </TableRow>
            )
          })}
        </Table>
      </FadeFromTop>
      <Grow
        in={!!selectedStories.length}
        style={{ position: "fixed", bottom: 48, right: 48 }}
      >
        <Fab color="primary" onClick={() => setExportModalOpen(true)}>
          <SendRounded />
        </Fab>
      </Grow>
      {/* export modal */}
      <Modal
        open={exportModalOpen}
        handleClose={() => setExportModalOpen(false)}
        title={t("create_a_journey")}
      >
        <ExportStoriesModalContent setExportModalOpen={setExportModalOpen} />
      </Modal>
    </PageContainer>
  )
}

const ExportStoriesModalContent = ({
  setExportModalOpen,
}: {
  setExportModalOpen: Dispatch<SetStateAction<boolean>>
}) => {
  const { t } = useTranslation()
  const { selectedStories, setSelectedStories } = useContext(StoriesContext)

  // data
  const [journeyTitle, setJourneyTitle] = useState<string>("")
  const [journeyStartDate, setJoruneyStartDate] = useState<string>("")
  const [journeyEndDate, setJoruneyEndDate] = useState<string>("")

  // conditions
  const conditions = {
    titleMaxLength: 50,
  }

  // download button disabled
  const buttonDisabled: boolean = useMemo(
    () =>
      !journeyTitle ||
      journeyTitle.length > conditions.titleMaxLength ||
      !journeyStartDate ||
      !journeyEndDate ||
      new Date(journeyStartDate) > new Date(journeyEndDate),
    [journeyTitle, journeyStartDate, journeyEndDate]
  )

  // errors
  const journeyTitleError = useMemo(
    () => journeyTitle.length > conditions.titleMaxLength,
    [journeyTitle]
  )
  const journeyEndDateError = useMemo(
    () => new Date(journeyStartDate) > new Date(journeyEndDate),
    [journeyStartDate, journeyEndDate]
  )

  // csv download button
  const csvButtonRef = useRef<any>(null)

  return (
    <Stack gap={2} style={{ width: "100%" }}>
      <Stack gap={1}>
        <Text id="journey-title" fontWeight={500}>
          {t("journey_title_input_label")}
        </Text>
        <Input
          value={journeyTitle}
          onChange={(e) => setJourneyTitle(e.target.value)}
          placeholder={t("journey_title_input_placeholder")}
          labelledBy="journey-title"
          helperText={journeyTitleError ? t("journey_title_input_error") : ""}
          error={journeyTitleError}
        />
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} gap={2}>
        <Stack gap={1} style={{ width: "100%" }}>
          <Text id="journey-start" fontWeight={500}>
            {t("journey_start_input_label")}
          </Text>
          <Input
            value={journeyStartDate}
            onChange={(e) => setJoruneyStartDate(e.target.value)}
            type="datetime-local"
            labelledBy="journey-start"
          />
        </Stack>
        <Stack gap={1} style={{ width: "100%" }}>
          <Text id="journey-end" fontWeight={500}>
            {t("journey_end_input_label")}
          </Text>
          <Input
            value={journeyEndDate}
            onChange={(e) => setJoruneyEndDate(e.target.value)}
            type="datetime-local"
            labelledBy="journey-end"
            helperText={journeyEndDateError ? t("journey_end_input_error") : ""}
            error={journeyEndDateError}
          />
        </Stack>
      </Stack>
      <Stack gap={1} style={{ width: "100%" }}>
        <Text fontWeight={500}>{t("journey_stories_list_title")}</Text>
        <Stack
          style={{
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "rgba(17, 67, 97, 0.08) 0px 0px 24px 0px",
          }}
        >
          {selectedStories.map((story, index) => (
            <Stack
              key={story.id}
              direction="row"
              alignItems="center"
              gap={2}
              style={{
                backgroundColor:
                  index % 2 === 0 ? "#F2F3F4" : colors.backgroundWhite,
                paddingInline: 16,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <img
                src={story.image}
                style={{
                  width: 30,
                  minWidth: 30,
                  height: 30,
                  borderRadius: 4,
                  objectFit: "cover",
                  objectPosition: "center",
                }}
                alt=""
              />
              <Text fontSize={14} fontWeight={500} ellipsis style={{ flex: 1 }}>
                {story.translation.title}
              </Text>
              <IconButton
                style={{ width: 30, height: 30 }}
                onClick={() =>
                  setSelectedStories((current) => {
                    const newArray = current.filter(
                      (item) => item.id !== story.id
                    )

                    if (!newArray.length) {
                      setExportModalOpen(false)
                    }

                    return current.filter((item) => item.id !== story.id)
                  })
                }
              >
                <CloseRounded style={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Button
        primary
        size="normal"
        disabled={buttonDisabled}
        style={{ marginTop: 22 }}
        onClick={() => csvButtonRef.current.link.click()}
      >
        {t("journey_download_document")}
      </Button>
      <CSVLink
        data={
          journeyStartDate && journeyEndDate
            ? [
                ["Title", "Start", "End", "Stories IDs", "Stories Titles"],
                [
                  journeyTitle,
                  new Date(journeyStartDate).toISOString(),
                  new Date(journeyEndDate).toISOString(),
                  selectedStories[0].id,
                  selectedStories[0].translation.title,
                ],
                ...selectedStories
                  .slice(1)
                  .map((story) => [
                    "",
                    "",
                    "",
                    story.id,
                    story.translation.title,
                  ]),
              ]
            : []
        }
        ref={csvButtonRef}
        filename={`${slugify(journeyTitle)}.csv`}
        style={{
          display: "none",
        }}
      />
    </Stack>
  )
}

export default Stories
